// Type definition for an Association object.

import {ReferenceReadWText} from "./reference";
import {DocumentRequestRead} from "./document_request";
import {TagType} from "./enums";



export interface AssociationRead {
    tag_id: string,
    doc_id: string,
    operator_labeled: boolean,
    score?: number;
    is_positive: boolean,
}

export interface AssociationReadWReferenceLoad extends AssociationRead {
    references: ReferenceReadWText[];
}

export interface AssociationReadWReferenceLoadAndTagInfo extends AssociationReadWReferenceLoad {
    tagName: string;
    tagType: TagType;
}

export function mergeAssociationsForRequest(
    docRequest: DocumentRequestRead,
    associations: AssociationReadWReferenceLoadAndTagInfo[]
): AssociationReadWReferenceLoadAndTagInfo {
    if (!associations.length) {
        throw new Error("Cannot merge: no associations provided");
    }

    // ✅ Precompute valid tag IDs from the DocumentRequest
    const validTagIds = new Set(docRequest.tags.map(t => t.id));

    // ✅ Verify all associations refer to tags belonging to this DocumentRequest
    for (const assoc of associations) {
        if (!validTagIds.has(assoc.tag_id)) {
            throw new Error(
                `Invalid association: tag_id ${assoc.tag_id} not part of DocumentRequest ${docRequest.id}`
            );
        }
    }

    // ✅ Merge logic
    let maxScore = -Infinity;
    let operatorLabeled = false;
    let isPositive = false;

    for (const a of associations) {
        if (a.score === undefined || a.score === null) continue;
        if (a.score > maxScore) maxScore = a.score;
        if (a.operator_labeled) operatorLabeled = true;
        if (a.is_positive) isPositive = true;
    }

    return {
        tag_id: docRequest.id, // 👈 aggregated association belongs to the DocumentRequest itself
        doc_id: associations[0].doc_id, // assuming all for same document
        operator_labeled: operatorLabeled,
        references: [], // merged association has no direct references
        is_positive: isPositive,
        score: maxScore === -Infinity ? 0 : maxScore,
        tagName: docRequest.name,
        tagType: TagType.REQUEST_SET,
    };
}


export type AssociationCreate = Omit<AssociationRead, "tag_id" | "doc_id">;

export type AssociationUpdate = Omit<AssociationRead, "tag_id" | "doc_id"> &
    { tag_id: string, doc_id: string };
